import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import type { FormEvent } from "react";
import { authFlowHeaderIcons } from "./authFlowHeaderIcons";
import {
  getLoginFieldSx,
  getLoginPrimaryButtonSx,
  LoginCardContent,
  LoginFormHeader,
} from "./loginUi";

export type ChangePasswordFormProps = {
  onSubmit?: (oldPassword: string, newPassword: string) => void;
  error?: string;
  loading?: boolean;
  translations: {
    changePassword: string;
    changePasswordInstructions: string;
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
    changeMyPassword: string;
    fieldRequired: string;
    passwordsDoNotMatch: string;
    passwordTooShort: string;
  };
};

export const ChangePasswordForm = ({
  onSubmit,
  error,
  loading = false,
  translations: t,
}: ChangePasswordFormProps) => {
  const theme = useTheme();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [oldPasswordError, setOldPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  const validate = (): boolean => {
    let valid = true;
    setOldPasswordError("");
    setNewPasswordError("");
    setConfirmError("");

    if (!oldPassword) {
      setOldPasswordError(t.fieldRequired);
      valid = false;
    }

    if (!newPassword) {
      setNewPasswordError(t.fieldRequired);
      valid = false;
    } else if (newPassword.length < 8) {
      setNewPasswordError(t.passwordTooShort);
      valid = false;
    }

    if (!confirmPassword) {
      setConfirmError(t.fieldRequired);
      valid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmError(t.passwordsDoNotMatch);
      valid = false;
    }

    return valid;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit?.(oldPassword, newPassword);
    }
  };

  const fieldSx = getLoginFieldSx(theme);

  return (
    <LoginCardContent
      component="form"
      onSubmit={handleSubmit}
      testId="change-password-form"
    >
      <LoginFormHeader
        title={t.changePassword}
        subtitle={t.changePasswordInstructions}
        headerIcon={authFlowHeaderIcons.changePassword}
        headerTestId="change-password-form-header"
        titleTestId="change-password-form-title"
        headerIconTestId="change-password-form-header-icon"
      />

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2.5 }}
          data-testid="change-password-error"
        >
          {error}
        </Alert>
      ) : null}

      <TextField
        fullWidth
        label={t.oldPassword}
        type="password"
        value={oldPassword}
        onChange={(e) => setOldPassword(e.target.value)}
        error={!!oldPasswordError}
        helperText={oldPasswordError}
        autoComplete="current-password"
        autoFocus
        sx={fieldSx}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            inputProps: { "data-testid": "old-password-input" },
          },
        }}
      />

      <TextField
        fullWidth
        label={t.newPassword}
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        error={!!newPasswordError}
        helperText={newPasswordError}
        autoComplete="new-password"
        sx={fieldSx}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            inputProps: { "data-testid": "new-password-input" },
          },
        }}
      />

      <TextField
        fullWidth
        label={t.confirmPassword}
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!confirmError}
        helperText={confirmError}
        autoComplete="new-password"
        sx={{ ...fieldSx, mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            inputProps: { "data-testid": "confirm-password-input" },
          },
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        data-testid="change-password-submit"
        sx={getLoginPrimaryButtonSx(theme)}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          t.changeMyPassword
        )}
      </Button>
    </LoginCardContent>
  );
};
